import { useState } from "react";

/**
 * Проверка валидности логина для входа
 * @param {string} login - проверяемый логин
 * @returns {boolean} - true если логин валиден
 */
export const isValidLogin = (login) => {
  if (!login || login.trim() === "") return false;

  const emailRegex =
    /^[A-Za-z0-9](?:[A-Za-z0-9._%+-]*[A-Za-z0-9])?@(?:[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?\.)+[A-Za-z]{2,}$/;

  return emailRegex.test(login);
};

/**
 * Проверка валидности пароля для входа
 * @param {string} password - проверяемый пароль
 * @returns {boolean} - true если пароль валиден
 */
export const isValidPassword = (password) => {
  if (!password || password.trim() === "") return false;

  if (password.length < 8) return false;

  const latinRegex = /^[A-Za-z0-9]+$/;
  if (!latinRegex.test(password)) return false;

  const hasLetter = /[A-Za-z]/.test(password);
  const hasDigit = /\d/.test(password);

  return hasLetter && hasDigit;
};

/**
 * Получение сообщения об ошибке для логина при входе
 * @param {string} login - проверяемый логин
 * @returns {string|null} - сообщение об ошибке или null
 */
export const getLoginErrorMessage = (login) => {
  if (!login || login.trim() === "") {
    return "Логин обязателен для заполнения";
  }

  const isValid = isValidLogin(login);
  return isValid ? null : "Неверный логин";
};

/**
 * Получение сообщения об ошибке для пароля при входе
 * @param {string} password - проверяемый пароль
 * @returns {string|null} - сообщение об ошибке или null
 */
export const getPasswordErrorMessage = (password) => {
  if (!password || password.trim() === "") {
    return "Пароль обязателен для заполнения";
  }

  const isValid = isValidPassword(password);
  return isValid ? null : "Неверный пароль";
};

/**
 * Полная валидация формы входа
 * @param {Object} formData - данные формы { login, password }
 * @returns {Object} - объект с ошибками валидации
 */
export const validateLoginForm = (formData) => {
  return {
    login: getLoginErrorMessage(formData.login),
    password: getPasswordErrorMessage(formData.password),
  };
};

/**
 * Хук для валидации формы входа
 * @param {Object} initialFormData - начальные данные формы
 * @param {Function} onChangeClearError - функция для очистки глобальной ошибки
 * @returns {Object} - объект с состоянием и функциями валидации
 */
export const useLoginFormValidation = (
  initialFormData = { login: "", password: "" },
  onChangeClearError = null,
) => {
  const [formData, setFormData] = useState(initialFormData);
  const [fieldErrors, setFieldErrors] = useState({
    login: "",
    password: "",
  });
  const [touched, setTouched] = useState({
    login: false,
    password: false,
  });

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (onChangeClearError) {
      onChangeClearError();
    }
  };

  const handleBlur = (field, value) => {
    setTouched((prev) => ({ ...prev, [field]: true }));

    const error =
      field === "login"
        ? getLoginErrorMessage(value)
        : getPasswordErrorMessage(value);

    setFieldErrors((prev) => ({ ...prev, [field]: error || "" }));
  };

  const validateForm = () => {
    const loginError = getLoginErrorMessage(formData.login);
    const passwordError = getPasswordErrorMessage(formData.password);

    const errors = {
      login: loginError || "",
      password: passwordError || "",
    };

    setFieldErrors(errors);
    setTouched({ login: true, password: true });

    return {
      isValid: !loginError && !passwordError,
      errors,
    };
  };

  const resetForm = () => {
    setFormData(initialFormData);
    setFieldErrors({ login: "", password: "" });
    setTouched({ login: false, password: false });
  };

  return {
    formData,
    fieldErrors,
    touched,
    handleInputChange,
    handleBlur,
    validateForm,
    resetForm,
    setFormData,
  };
};
